import ListLoader from "@components/common/ListLoader.jsx";
import { capitalizeFirst } from "@helpers/stringFormatters.js";
import { useClickOutside } from "@hooks/useClickOutside.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";

function SelectFilter({
	id,
	label,
	value,
	placeholder,
	onChange,
	options,
	isLoading,
	hasMore,
	onLoadMore,
	searchTerm,
	onSearchChange,
	optionLabel,
	optionValue,
	showSearch = true,
	searchPlaceholder = "Search for ...",
	isPaginated = true,
	disabled = false,
	dataTestId,
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [localSearchTerm, setLocalSearchTerm] = useState("");
	const dropdownRef = useRef(null);
	const loadMoreRef = useRef(null);
	const scrollContainerRef = useRef(null);

	useClickOutside(dropdownRef, () => setIsOpen(false));

	// Client-side filtering for non-paginated data
	const filteredOptions = useMemo(() => {
		if (isPaginated) {
			return options; // Use options as-is for paginated data
		}

		// Client-side filtering for non-paginated data
		if (!localSearchTerm.trim()) {
			return options;
		}

		return options.filter((option) => {
			const label = option?.[optionLabel]?.toString().toLowerCase() || "";
			return label.includes(localSearchTerm.toLowerCase());
		});
	}, [options, localSearchTerm, isPaginated, optionLabel]);

	useEffect(() => {
		if (!isOpen) return;
		if (!hasMore || isLoading) return;
		if (!scrollContainerRef.current) return;
		if (!loadMoreRef.current) return;
		if (!isPaginated) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					onLoadMore();
				}
			},
			{
				root: scrollContainerRef.current,
				threshold: 0.1,
			},
		);
		observer.observe(loadMoreRef.current);

		return () => observer.disconnect();
	}, [isOpen, hasMore, isLoading, options?.length, onLoadMore]);

	const onSelectChange = (item) => {
		if (disabled) return;
		if (onChange) onChange(item);
		setIsOpen(false);
		// Reset local search when selection is made
		if (!isPaginated) {
			setLocalSearchTerm("");
		}
	};

	const onClearClick = (e) => {
		e.stopPropagation();
		if (disabled) return;
		onChange && onChange(null);
	};

	const onSearch = (e) => {
		const value = e.target.value;

		if (isPaginated) {
			// Server-side search for paginated data
			if (showSearch && onSearchChange) {
				onSearchChange(value);
			}
		} else {
			// Client-side search for non-paginated data
			setLocalSearchTerm(value);
		}
	};

	// Determine which search term to display
	const displaySearchTerm = isPaginated ? searchTerm : localSearchTerm;

	// Reset local search when dropdown closes
	useEffect(() => {
		if (!isOpen && !isPaginated) {
			setLocalSearchTerm("");
		}
	}, [isOpen, isPaginated]);

	const handleDropdownToggle = () => {
		if (disabled) return;
		setIsOpen(!isOpen);
	};

	const baseTestId = dataTestId || id;

	return (
		<div data-testid={`${baseTestId}-container`}>
			<label
				htmlFor={id}
				className={`mb-2 flex items-center text-left font-normal text-xs ${
					disabled ? "text-gray-500" : "text-gray-400"
				}`}
			>
				{label}
			</label>
			<div data-testid={`${baseTestId}-trigger`} className="relative" ref={dropdownRef}>
				{/* Dropdown trigger */}
				<div
					onClick={handleDropdownToggle}
					className={`flex w-full items-center justify-between rounded-lg border border-gray-600 bg-blue-gray bg-opacity-20 p-2.5 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray ${
						disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer text-gray-400"
					}`}
				>
					<span className="flex-1 truncate text-left" data-testid={`${baseTestId}-value`}>
						{value?.[optionLabel] ? capitalizeFirst(value?.[optionLabel]) : placeholder}
					</span>
					<IconButton value={value} isOpen={isOpen} onClear={onClearClick} disabled={disabled} />
				</div>

				{/* Dropdown menu */}
				{isOpen && !disabled && (
					<div
						data-testid={`${baseTestId}-scrollcontainer`}
						ref={scrollContainerRef}
						className="custom-scrollbar absolute z-50 mt-4 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-600 bg-dropdown-bg"
					>
						{/* Search Input */}
						{showSearch && (
							<input
								data-testid={`${baseTestId}-search`}
								type="text"
								value={displaySearchTerm}
								onChange={onSearch}
								placeholder={searchPlaceholder}
								className="sticky top-0 z-10 w-full border-gray-600 border-x-0 border-t-0 border-b bg-navbar-bg p-2.5 text-gray-400 text-xs placeholder-gray-400 outline-none focus:border-gray-600 focus:outline-none focus:ring-0"
								onClick={(e) => e.stopPropagation()}
							/>
						)}

						{/* Loading State */}
						{isLoading && filteredOptions.length === 0 ? (
							<div className="p-2 text-center text-gray-400">
								<ListLoader />
							</div>
						) : filteredOptions?.length > 0 ? (
							<>
								<ul className="text-start text-sm">
									{filteredOptions?.map((option) => (
										<li
											key={option?.[optionValue]}
											className={`cursor-pointer p-2 text-sm text-white hover:bg-dropdown-hover ${
												value?.[optionValue] === option?.[optionValue]
													? "bg-blue-gray bg-opacity-80"
													: ""
											}`}
											onClick={() => onSelectChange(option)}
										>
											{capitalizeFirst(option?.[optionLabel])}
										</li>
									))}
								</ul>

								{/* Infinite scroll trigger */}
								{isPaginated && hasMore && (
									<div ref={loadMoreRef} className="p-2 text-center text-gray-400 text-sm">
										{isLoading && <ListLoader />}
									</div>
								)}
							</>
						) : (
							<div className="p-2 text-gray-400">
								{displaySearchTerm ? "No results found" : "No options available"}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

function IconButton({ value, isOpen = false, onClear, disabled = false, dataTestId }) {
	const baseTestId = dataTestId;

	return (
		<>
			{value && !disabled && (
				<FaTimes
					data-testid={`${baseTestId}-clear`}
					className="ml-2 cursor-pointer text-gray-400"
					onClick={onClear}
				/>
			)}
			<FaChevronDown
				className={`ml-2 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""} ${
					disabled ? "opacity-50" : ""
				}`}
			/>
		</>
	);
}
export default SelectFilter;
